import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { ToolForm } from '@/organism/BossBaby';
import { BackButton } from '@/organism';
import {
  CreateBossBabyToolDto,
  UpdateBossBabyToolDto,
  BossBabyToolDto
} from '@/dto/BossBabyDto';

interface ToolFormTemplateProps {
  initialData?: BossBabyToolDto;
  onSubmit: (data: CreateBossBabyToolDto | UpdateBossBabyToolDto) => void;
  onCancel: () => void;
  isEdit?: boolean;
  loading?: boolean;
}

export const ToolFormTemplate: React.FC<ToolFormTemplateProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false,
  loading = false,
}) => {
  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
            Boss Baby Tools
          </Link>
          <Typography color="text.primary">
            {isEdit ? 'Edit Tool' : 'Create Tool'}
          </Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ mb: 1 }}>
          {isEdit ? `Edit Tool: ${initialData?.toolName}` : 'Create New Boss Baby Tool'}
        </Typography>

        <Typography variant="body1" color="text.secondary">
          {isEdit
            ? 'Modify the tool configuration and settings'
            : 'Configure a new Boss Baby tool with its schema and parameters'
          }
        </Typography>
      </Box>

      {/* Back Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          onClick={onCancel}
          startIcon={<ArrowBackIcon />}
          color="inherit"
        >
          Back to List
        </Button>
      </Box>

      {/* Form Content */}
      {loading ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Loading tool data...</Typography>
        </Paper>
      ) : (
        <ToolForm
          initialData={initialData}
          onSubmit={onSubmit}
          onCancel={onCancel}
          isEdit={isEdit}
        />
      )}

      {/* Help Text */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Boss Baby Tool Guidelines
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>Tool Name:</strong> Use descriptive names that clearly indicate the tool's purpose.
          Examples: ScheduleManagementTool, EmailNotificationTool, CalendarIntegrationTool.
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>Schema:</strong> Define the tool's parameters using JSON Schema format.
          This determines what inputs the tool can accept from Boss Baby tasks.
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>Priority Level:</strong>
          • <strong>HIGH:</strong> Critical tools that must be processed immediately
          • <strong>MEDIUM:</strong> Standard tools with normal processing priority
          • <strong>LOW:</strong> Non-critical tools that can be delayed if needed
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>Timeout:</strong> Maximum execution time in seconds.
          Consider the complexity of operations when setting this value.
        </Typography>

        <Typography variant="body2">
          <strong>Categories:</strong>
          • <strong>boss_baby:</strong> Core Boss Baby functionality
          • <strong>utility:</strong> General utility tools
          • <strong>integration:</strong> External service integrations
        </Typography>
      </Paper>
    </Box>
  );
};